import type {
  DocumentPartySnapshotRecord,
  PaymentAgreementInput,
  PaymentCurrency,
  PaymentScheduleSnapshotRecord
} from '@shared/types';

const DEFAULT_DELIVERY_WORKING_DAYS = 10;
const DEFAULT_REVISION_ROUNDS = 2;
const DEFAULT_ACCEPTANCE_WORKING_DAYS = 5;
const DEFAULT_WARRANTY_DAYS = 14;

export interface PaymentAgreementBuildInput {
  title: string;
  serviceSummary: string;
  amountMinor: number;
  currency: PaymentCurrency;
  issuer: DocumentPartySnapshotRecord;
  customer: DocumentPartySnapshotRecord;
  agreement?: PaymentAgreementInput | null | undefined;
  paymentScheduleSnapshot?: PaymentScheduleSnapshotRecord | null | undefined;
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.replace(/\s+/g, ' ') : null;
}

function cleanText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function withFallback(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const cleaned = clean(value);
    if (cleaned) {
      return cleaned;
    }
  }

  return 'Nespecificat';
}

function withTextFallback(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const cleaned = cleanText(value);
    if (cleaned) {
      return cleaned;
    }
  }

  return 'Nespecificat';
}

function positiveInteger(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;
}

function nonNegativeInteger(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value) : fallback;
}

function formatAgreementDate(value: string | null | undefined): string {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;

  return new Intl.DateTimeFormat('ro-RO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(safeDate);
}

function formatAmount(amountMinor: number, currency: PaymentCurrency): string {
  const amountMajor = amountMinor / 100;

  return new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: amountMajor % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  }).format(amountMajor) + ` ${currency}`;
}

function partyAddress(snapshot: DocumentPartySnapshotRecord): string {
  return withFallback(snapshot.signingAddress?.formatted, snapshot.billingAddress?.formatted);
}

function partyRepresentative(snapshot: DocumentPartySnapshotRecord, override: string | null | undefined): string {
  return withFallback(override, snapshot.representative.name, snapshot.legalName, snapshot.displayName);
}

export function buildPaymentAgreementText(input: PaymentAgreementBuildInput): string {
  const agreement = input.agreement ?? null;
  const agreementDate = formatAgreementDate(agreement?.agreementDate);
  const issuerName = withFallback(input.issuer.legalName, input.issuer.displayName);
  const issuerAddress = partyAddress(input.issuer);
  const issuerCui = withFallback(input.issuer.cui);
  const issuerRegistrationNumber = withFallback(input.issuer.registrationNumber);
  const issuerRepresentative = partyRepresentative(input.issuer, agreement?.issuerRepresentativeName);
  const customerName = withFallback(agreement?.customerLegalName, input.customer.legalName, input.customer.displayName);
  const customerIdentifier = withFallback(agreement?.customerIdentifier, input.customer.cui);
  const customerAddress = withFallback(agreement?.customerAddress, input.customer.signingAddress?.formatted, input.customer.billingAddress?.formatted);
  const customerRepresentative = partyRepresentative(input.customer, agreement?.customerRepresentativeName);
  const projectDescription = withTextFallback(agreement?.projectDescription, input.serviceSummary, input.title);
  const paymentTerms = withTextFallback(
    agreement?.paymentTermsText,
    input.paymentScheduleSnapshot?.scheduleSummary,
    'conform paginii de plată și/sau ofertei acceptate de Client'
  );
  const deliveryWorkingDays = positiveInteger(agreement?.deliveryWorkingDays, DEFAULT_DELIVERY_WORKING_DAYS);
  const revisionRounds = nonNegativeInteger(agreement?.revisionRounds, DEFAULT_REVISION_ROUNDS);
  const acceptanceWorkingDays = positiveInteger(agreement?.acceptanceWorkingDays, DEFAULT_ACCEPTANCE_WORKING_DAYS);
  const warrantyDays = positiveInteger(agreement?.warrantyDays, DEFAULT_WARRANTY_DAYS);
  const amount = formatAmount(input.amountMinor, input.currency);

  return [
    'Contract Prestări Servicii Web Development',
    `Încheiat astăzi, ${agreementDate}, între:`,
    `Prestator: ${issuerName}, cu sediul în ${issuerAddress}, CUI ${issuerCui}, Nr. Reg. Com. ${issuerRegistrationNumber}, reprezentată prin ${issuerRepresentative}, denumită în continuare „Prestatorul”.`,
    `Client: ${customerName}, CUI/CNP ${customerIdentifier}, cu sediul/domiciliul în ${customerAddress}, reprezentat prin ${customerRepresentative}, denumit în continuare „Clientul”.`,
    `1. Obiectul contractului\nPrestatorul va realiza pentru Client servicii de web development/design conform ofertei acceptate de Client: ${projectDescription}. Oferta, factura proformă, pagina de plată sau mesajul scris acceptat de Client fac parte din prezentul contract.`,
    `2. Preț și plată\nPrețul proiectului este de ${amount} + TVA, dacă este cazul. Plata se face astfel: ${paymentTerms}. Prestatorul poate începe lucrul doar după confirmarea plății avansului. Factura fiscală se emite conform legislației aplicabile.`,
    `3. Termen de livrare\nTermenul estimat de livrare este de ${deliveryWorkingDays} zile lucrătoare de la primirea plății și a tuturor materialelor necesare de la Client. Întârzierile cauzate de lipsa materialelor, feedbackului sau acceselor necesare pot prelungi termenul de livrare.`,
    '4. Obligațiile Clientului\nClientul se obligă să furnizeze la timp textele, imaginile, datele firmei, accesul la domeniu/hosting și orice alte informații necesare. Clientul răspunde pentru legalitatea materialelor furnizate, inclusiv texte, imagini, logo-uri, date personale, mențiuni legale și informații comerciale.',
    `5. Revizii incluse\nPrețul include maximum ${revisionRounds} runde de revizii. O revizie înseamnă modificări rezonabile asupra materialului livrat, pe baza direcției aprobate inițial. Nu sunt considerate revizii și se taxează separat: schimbarea completă a direcției de design, pagini noi, funcționalități noi, refacerea integrală a structurii, modificări majore de conținut sau cerințe care nu au fost incluse în oferta inițială.`,
    `6. Acceptarea lucrării\nPrestatorul va trimite Clientului o variantă de previzualizare. Clientul are la dispoziție ${acceptanceWorkingDays} zile lucrătoare pentru a transmite observații scrise. Dacă Clientul nu transmite observații în acest termen, lucrarea se consideră acceptată. Publicarea website-ului sau folosirea materialelor livrate reprezintă acceptarea lucrării.`,
    '7. Drepturi de proprietate intelectuală\nDupă plata integrală, Clientul primește dreptul de utilizare asupra website-ului/materialelor realizate special pentru el. Prestatorul își păstrează drepturile asupra instrumentelor, componentelor generale, codului reutilizabil, know-how-ului, template-urilor și proceselor proprii folosite în proiect. Până la plata integrală, toate materialele livrate rămân proprietatea Prestatorului.',
    '8. Servicii terțe\nCosturile pentru domeniu, hosting, licențe, pluginuri, teme, fonturi, servicii de email, servicii de plată, stock photos sau alte servicii externe nu sunt incluse, cu excepția cazului în care oferta spune expres altceva. Prestatorul nu răspunde pentru întreruperi, modificări sau probleme cauzate de furnizori terți.',
    `9. Garanție și mentenanță\nPrestatorul oferă ${warrantyDays} zile de corectare gratuită pentru erori tehnice apărute în funcționalitățile incluse în proiect. Garanția nu include modificări noi, actualizări de conținut, probleme cauzate de intervenții externe, pluginuri, hosting, parole compromise sau servicii terțe. Mentenanța lunară se contractează separat.`,
    '10. Limitarea răspunderii\nPrestatorul va depune toate eforturile rezonabile pentru livrarea unui proiect profesionist, dar nu garantează anumite rezultate comerciale, poziții SEO, număr de clienți, vânzări sau performanțe dependente de factori externi. Răspunderea totală a Prestatorului este limitată la suma efectiv plătită de Client pentru proiectul respectiv.',
    '11. Încetarea contractului\nDacă Clientul întârzie plata, materialele sau feedbackul mai mult de 10 zile lucrătoare, Prestatorul poate suspenda proiectul. Sumele plătite pentru munca deja realizată nu sunt rambursabile. Dacă proiectul este anulat de Client după începerea lucrului, Prestatorul poate păstra suma corespunzătoare muncii realizate până la acel moment.',
    '12. Confidențialitate\nPărțile se obligă să păstreze confidențiale informațiile comerciale, tehnice sau financiare primite în timpul colaborării, cu excepția informațiilor publice sau cerute de lege.',
    '13. Portofoliu\nPrestatorul poate menționa proiectul în portofoliu, website, social media sau materiale de prezentare, cu excepția cazului în care Clientul solicită în scris confidențialitate înainte de începerea proiectului.',
    '14. Legea aplicabilă\nContractul este guvernat de legea română. Orice neînțelegere va fi soluționată amiabil, iar dacă acest lucru nu este posibil, de instanțele competente din România.',
    `Semnat / acceptat astăzi, ${agreementDate}.`,
    `Prestator: ${issuerName}\nNume: ${issuerRepresentative}\nSemnătură: __________`,
    `Client: ${customerName}\nNume: ${customerRepresentative}\nSemnătură: __________`
  ].join('\n\n');
}
